import type Application from "../../types/application";
import CompanyLogo from "@components/company_logo";
import { Body, SectionHeader, SectionTitle } from "@sprout/typography";
import CheckMarkCircleIcon from "@sprout/icons/check_mark_circle";
import { Row, Stack } from "@sprout/layout";
import useTranslation from "@hooks/use_translation";
import { Tag } from "@sprout/tag.tsx";
import theme from "@/stylesheets/theme.module.scss";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover_card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@components/ui/dialog";
import { useState } from "react";
import { router } from "@inertiajs/react";
import useCsrfToken from "@/hooks/use_csrf";
import Button from "@/sprout/button";
import { queryClient } from "@/queries/query_client";
import HelpPopover from "@sprout/help_popover.tsx";
import ApplicationEllipsis from "@components/applications/application_ellipsis.tsx";

type Props = {
  application: Application;
  hasTokens: boolean;
  onAddDreamJob: () => void;
};

const ApplicationHeader = (props: Props) => {
  const { application } = props;
  const [isInactiveModalOpen, setIsInactiveModalOpen] = useState(false);
  const { t } = useTranslation("applications");
  const csrfToken = useCsrfToken();

  const handleMarkInactiveConfirm = () => {
    router.patch(
      `/applications/${application.id}/mark_inactive`,
      {
        authenticity_token: csrfToken,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["applications"] });
          router.visit("/applications");
        },
      },
    );
  };

  const openInactiveModal = () => setIsInactiveModalOpen(true);

  return (
    <Row gap="24px" align="flex-start">
      <div className="flex flex-col md:flex-row w-full gap-2 md:gap-6 content-center">
        <div className="hidden md:block">
          <CompanyLogo
            logoUrl={application.company_logo_url}
            companyName={application.company_name}
            size="large"
          />
        </div>
        <div className="flex flex-row md:hidden">
          <div className="grow">
            <CompanyLogo
              logoUrl={application.company_logo_url}
              companyName={application.company_name}
              size="medium"
            />
          </div>
          <ApplicationEllipsis openInactiveModal={openInactiveModal} {...props} />
        </div>

        <Stack gap="4px" className="justify-center">
          {application.dream_job && <Tag label={t("dream_job_tag")} color={theme.marigoldE50} />}
          <SectionHeader>{application.job_title}</SectionHeader>
          <div className="flex flex-wrap items-center gap-2">
            <SectionTitle>{application.company_name}</SectionTitle>
            {application.hasStatus ? (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-2 cursor-pointer">
                    <CheckMarkCircleIcon size="sm" viewBox="0 0 16 16" color="green" />
                    <Body>{t("sharing_updates")}</Body>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent>
                  {t("sharing_updates_tooltip", { company_name: application.company_name })}
                </HoverCardContent>
              </HoverCard>
            ) : (
              <div className="flex items-center gap-2">
                <HelpPopover
                  text={t("not_sharing_updates_tooltip", {
                    company_name: application.company_name,
                  })}
                  className="flex"
                />
                <Body className="!text-gray-800">{t("not_sharing_updates")}</Body>
              </div>
            )}
          </div>
        </Stack>
      </div>
      <div className="hidden md:block">
        <ApplicationEllipsis openInactiveModal={openInactiveModal} {...props} />
      </div>

      {!application.inactive && (
        <Dialog open={isInactiveModalOpen} onOpenChange={setIsInactiveModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <SectionTitle>{t("inactive_modal_title")}</SectionTitle>
              </DialogTitle>
            </DialogHeader>
            <DialogDescription>
              <Body>{t("inactive_modal_body")}</Body>
            </DialogDescription>
            <DialogFooter>
              <DialogClose asChild>
                <Button>{t("inactive_modal_cancel")}</Button>
              </DialogClose>
              <Button onClick={handleMarkInactiveConfirm} error>
                {t("inactive_modal_confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Row>
  );
};

export default ApplicationHeader;
